'use client';

import React, { createContext, useContext, useState } from 'react';
import { fetchAPI } from '@/core/api/client';
import { localizePath, marketOrigin, resolveMarket, type Market } from '@/core/i18n/markets';
import { localizeRoute, toInternalRoute } from '@/core/i18n/routes';
import { localizeHref } from '@/core/i18n/links';

type TranslationContextType = {
  locale: string;
  currency: string;
  t: (key: string, fallback?: string) => string;
  tPath: (path: string) => string;
  setLocale: (locale: string, redirectPath?: string) => void;
  setCurrency: (currency: string) => void;
  setCurrencyQuiet: (currency: string) => void;
  supportedLocales: string[];
  supportedCurrencies: string[];
  defaultLocale: string;
  /** The storefront market of this page (absent in admin/studio). */
  market?: Market;
  /** Classic header/footer texts in this page's language (server-translated, see chrome_labels.py). */
  labelTranslations: Record<string, string>;
};

const ONE_YEAR = 365 * 24 * 60 * 60;

// The language-specific slug of the page the visitor is on, if it has one (translations[x].slug
// on products, categories and builder pages). Without it a switch would land on the source
// language's slug under the new language's address.
async function translatedPath(internalPath: string, newLocale: string): Promise<string> {
  const [pathname, query = ''] = internalPath.split('?');
  const suffix = query ? `?${query}` : '';
  try {
    const product = pathname.match(/^\/produkty\/([^/]+)$/);
    if (product) {
      const slug = (await fetchAPI(`/products/${product[1]}`))?.translations?.[newLocale]?.slug;
      return slug ? `/produkty/${slug}${suffix}` : internalPath;
    }
    const category = pathname.match(/^\/kategoria\/(.+)$/);
    if (category) {
      const slug = (await fetchAPI(`/categories/by-path/${category[1]}`))?.translations?.[newLocale]?.slug;
      return slug ? `/kategoria/${slug}${suffix}` : internalPath;
    }
    const page = pathname.match(/^\/([^/]+)$/);
    if (page) {
      const slug = (await fetchAPI(`/pages/${page[1]}`))?.translations?.[newLocale]?.slug;
      return slug ? `/${slug}${suffix}` : internalPath;
    }
  } catch {
    // Keep the current slug — the page route still finds the page by any of its slugs.
  }
  return internalPath;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({
  children,
  messages,
  labelTranslations = {},
  market,
  markets = [],
  primaryOrigin = '',
  initialLocale = 'pl',
  initialCurrency = 'PLN',
  supportedLocales = ['pl', 'en', 'de'],
  supportedCurrencies = ['PLN', 'EUR', 'USD', 'GBP', 'CZK'],
  defaultLocale = 'pl'
}: {
  children: React.ReactNode;
  /** The active language's dictionary, already merged with its fallbacks — getMessages() in
   *  core/utils/server-locale.ts. Language switches are full page loads, so it never changes here. */
  messages: Record<string, string>;
  /** Admin-typed header/footer texts translated into this page's language (source text → translation). */
  labelTranslations?: Record<string, string>;
  /** The storefront market this page belongs to (core/i18n/markets.ts); absent in admin/studio. */
  market?: Market;
  /** The shop's active markets — where each language lives, for the language switcher. */
  markets?: Market[];
  /** FRONTEND_URL: origin of the main address (default and prefix markets). */
  primaryOrigin?: string;
  initialLocale?: string;
  initialCurrency?: string;
  supportedLocales?: string[];
  supportedCurrencies?: string[];
  defaultLocale?: string;
}) {
  // The server already decided both from the URL (and, for a language without its own address,
  // the visitor's earlier choice) — see proxy.ts. Nothing is read from or written to cookies until
  // the visitor actually switches.
  const [locale, setLocaleState] = useState(initialLocale);
  const [currency, setCurrencyState] = useState(initialCurrency);

  const setLocale = async (newLocale: string, redirectPath?: string) => {
    if (!supportedLocales.includes(newLocale)) return;
    document.cookie = `stag_locale=${newLocale}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`;
    setLocaleState(newLocale);

    const here = markets.length
      ? resolveMarket(markets, window.location.host, window.location.pathname).internalPath
      : window.location.pathname;
    const internalPath = redirectPath || await translatedPath(`${toInternalRoute(here) ?? here}${window.location.search}`, newLocale);

    // A language with an address of its own lives there (another domain or a /xx/ prefix); any
    // other language is rendered on the main address from the cookie just set. Full navigation,
    // not router.push, so the server renders the new language.
    const target = markets.find((candidate) => candidate.locale === newLocale)
      || markets.find((candidate) => candidate.url_type === 'default');
    const path = localizeRoute(newLocale, internalPath);
    window.location.href = target
      ? `${marketOrigin(target, primaryOrigin || window.location.origin)}${localizePath(target, path)}`
      : path;
  };

  const setCurrency = (newCurrency: string) => {
    if (!supportedCurrencies.includes(newCurrency)) return;
    document.cookie = `stag_currency=${newCurrency}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`;
    setCurrencyState(newCurrency);
    window.location.reload();
  };

  // Same as setCurrency but without the reload — needed anywhere a currency switch is a side
  // effect of something else the user is actively doing (e.g. picking a shipping country
  // mid-checkout), where a full page reload would silently wipe whatever they'd already typed
  // into the form. The header currency switcher still uses the reloading setCurrency — that one's
  // a deliberate, standalone action where re-fetching localized content is the point.
  const setCurrencyQuiet = (newCurrency: string) => {
    if (!supportedCurrencies.includes(newCurrency)) return;
    document.cookie = `stag_currency=${newCurrency}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`;
    setCurrencyState(newCurrency);
  };

  const t = (key: string, fallback?: string): string => {
    const val = messages[key];
    if (val !== undefined) return val;
    return fallback !== undefined ? fallback : key;
  };

  // An app path ('/koszyk', '/produkty/x') as visitors address it here: the route named in the
  // page's language, under the market's /xx/ prefix when it has one. Idempotent — an already
  // localized path is returned unchanged, so wrapping a tPath() result again is harmless.
  const tPath = (path: string): string => localizeHref(path || '/', { locale, market });

  return (
    <TranslationContext.Provider value={{ locale, currency, t, tPath, setLocale, setCurrency, setCurrencyQuiet, supportedLocales, supportedCurrencies, defaultLocale, market, labelTranslations }}>
      {children}
    </TranslationContext.Provider>
  );
}

/** Like useTranslation, but null outside a TranslationProvider (shared components rendered anywhere). */
export function useOptionalTranslation() {
  return useContext(TranslationContext) ?? null;
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}

